import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import ManagerAvatar from '../components/ui/ManagerAvatar';
import RecordsUnderAttack from '../components/stats/RecordsUnderAttack';
import { parseCSV } from '../utils/csv';
import { ACTIVE_MEMBERS } from '../utils/managers';
import { loadCareerRankings } from '../utils/managerStats';
import { buildRecordsUnderAttack } from '../utils/recordsUnderAttack';
import { loadSeasonVault } from '../utils/seasonVault';
import { managerSlug } from '../utils/slug';
import { Reveal } from '../motion';

function formatNumber(value, digits = 0) {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function LeaderCard({ label, manager, value, copy, tone = 'gold' }) {
  if (!manager) return null;
  const border = tone === 'red' ? 'border-accent-red' : 'border-accent-gold';
  const color = tone === 'red' ? 'text-accent-red' : 'text-accent-gold';
  return (
    <Link to={`/managers/${managerSlug(manager)}`} className={`block bg-bg-surface border-t-[3px] ${border} p-5 hover:bg-bg-elevated transition-colors`}>
      <div className="flex items-center gap-3">
        <ManagerAvatar name={manager} size="sm" />
        <div className="min-w-0">
          <div className="text-[9px] tracking-[0.22em] text-ink-tertiary font-extrabold uppercase">{label}</div>
          <div className="mt-1 text-lg font-black uppercase truncate">{manager}</div>
        </div>
      </div>
      <div className={`mt-5 font-display text-4xl font-black uppercase leading-none ${color}`}>{value}</div>
      <p className="mt-2 text-[12px] text-ink-secondary leading-relaxed italic">{copy}</p>
    </Link>
  );
}

function GameRecordCard({ label, game, value, copy }) {
  if (!game) return null;
  return (
    <article className="bg-bg-surface border-l-[3px] border-accent-gold p-5">
      <div className="text-[9px] tracking-[0.22em] text-ink-tertiary font-extrabold uppercase">{label}</div>
      <div className="mt-2 font-display text-3xl md:text-4xl font-black uppercase leading-none text-accent-gold">{value}</div>
      <p className="mt-2 text-[12px] text-ink-secondary leading-relaxed italic">{copy}</p>
    </article>
  );
}

export default function Stats() {
  const [rankings, setRankings] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [standings, setStandings] = useState([]);

  useEffect(() => {
    Promise.all([
      loadCareerRankings(),
      loadSeasonVault(),
      fetch('/data/season_standings.csv').then((response) => response.text()).then(parseCSV),
    ])
      .then(([careerRows, seasonRows, standingsRows]) => {
        setRankings(careerRows);
        setSeasons(seasonRows);
        setStandings(standingsRows);
      })
      .catch(() => {
        setRankings([]);
        setSeasons([]);
        setStandings([]);
      });
  }, []);

  const milestones = useMemo(
    () => buildRecordsUnderAttack(rankings, standings, ACTIVE_MEMBERS),
    [rankings, standings],
  );

  const summary = useMemo(() => {
    const totalPoints = rankings.reduce((sum, row) => sum + row.stats.total_points, 0);
    const managerGames = rankings.reduce((sum, row) => sum + row.stats.total_games, 0);
    return {
      totalPoints,
      matchups: managerGames / 2,
      champions: rankings.filter((row) => row.stats.championships > 0).length,
    };
  }, [rankings]);

  const sortedByPoints = rankings.slice().sort((first, second) => (
    second.stats.total_points - first.stats.total_points
  ));
  const winsLeader = rankings.slice().sort((first, second) => second.stats.total_wins - first.stats.total_wins)[0];
  const pointsLeader = sortedByPoints[0];
  const ringsLeader = rankings.slice().sort((first, second) => (
    second.stats.championships - first.stats.championships
      || second.stats.win_percentage - first.stats.win_percentage
  ))[0];
  const rateLeader = rankings
    .filter((row) => row.stats.seasons_played >= 5)
    .sort((first, second) => second.stats.win_percentage - first.stats.win_percentage)[0];
  const weeklyHigh = seasons
    .map((season) => season.highestScore)
    .filter(Boolean)
    .sort((first, second) => second.score - first.score)[0];
  const closestGame = seasons
    .map((season) => ({ ...season.closestGame, year: season.year }))
    .filter((game) => Number.isFinite(game.margin))
    .sort((first, second) => first.margin - second.margin)[0];
  const largestTitleMargin = seasons
    .map((season) => ({ ...season.titleGame, year: season.year }))
    .filter((game) => Number.isFinite(game.margin))
    .sort((first, second) => second.margin - first.margin)[0];

  return (
    <>
      <HeroBackground variant="gold" backdrop="court" className="px-6 md:px-14 py-16">
        <Eyebrow>LEAGUE STATISTICS</Eyebrow>
        <h1
          className="font-display font-black uppercase tracking-normal mt-3.5 leading-[0.88]"
          style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}
        >
          Every season.<br />
          <span className="text-accent-gold">Every receipt.</span>
        </h1>
        <p className="mt-5 max-w-3xl text-base md:text-lg text-ink-secondary leading-relaxed italic">
          The complete regular-season ledger through 2025: lifetime scoring, records, playoff conversion, weekly extremes, and direct links into every manager's full career profile.
        </p>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-5 max-w-4xl">
          <div><div className="font-display text-4xl font-black text-accent-gold">{seasons.length || '—'}</div><div className="text-[9px] tracking-[0.2em] text-ink-tertiary">SEASONS</div></div>
          <div><div className="font-display text-4xl font-black text-accent-gold">{summary.matchups ? formatNumber(summary.matchups) : '—'}</div><div className="text-[9px] tracking-[0.2em] text-ink-tertiary">MATCHUPS</div></div>
          <div><div className="font-display text-4xl font-black text-accent-gold">{summary.totalPoints ? formatNumber(summary.totalPoints, 2) : '—'}</div><div className="text-[9px] tracking-[0.2em] text-ink-tertiary">POINTS</div></div>
          <div><div className="font-display text-4xl font-black text-accent-gold">{summary.champions || '—'}</div><div className="text-[9px] tracking-[0.2em] text-ink-tertiary">CHAMPIONS</div></div>
        </div>
      </HeroBackground>

      <section className="px-6 md:px-14 py-14 border-b border-border">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <Eyebrow color="gold" className="mb-2 tracking-[0.45em]">ALL-TIME TABLE</Eyebrow>
            <h2 className="text-display-xs md:text-display-sm font-black tracking-normal leading-none">The lifetime scoreboard.</h2>
          </div>
          <div className="text-[10px] text-ink-tertiary tracking-[0.16em] italic">Sorted by total regular-season points</div>
        </Reveal>
        <div className="md:hidden mb-3 text-[10px] text-accent-gold tracking-[0.16em] font-extrabold uppercase">
          Swipe table horizontally →
        </div>
        <Reveal delay={80} className="overflow-x-auto border border-border bg-bg-surface">
          <table className="w-full min-w-[1080px] text-left">
            <thead className="bg-bg-deep text-[9px] tracking-[0.2em] text-ink-tertiary uppercase">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Manager</th>
                <th className="px-4 py-3">Seasons</th>
                <th className="px-4 py-3">Record</th>
                <th className="px-4 py-3">Win %</th>
                <th className="px-4 py-3">Lifetime PF</th>
                <th className="px-4 py-3">PF / season</th>
                <th className="px-4 py-3">PF / game</th>
                <th className="px-4 py-3">Playoffs</th>
                <th className="px-4 py-3">Titles</th>
                <th className="px-4 py-3">Best streak</th>
              </tr>
            </thead>
            <tbody>
              {sortedByPoints.map((row, index) => (
                <tr key={row.manager} className="border-t border-border text-sm hover:bg-bg-elevated/60 transition-colors">
                  <td className="px-4 py-4 font-display text-xl font-black text-ink-tertiary">{index + 1}</td>
                  <td className="px-4 py-4">
                    <Link to={`/managers/${managerSlug(row.manager)}`} className="flex items-center gap-3 hover:text-accent-gold">
                      <ManagerAvatar name={row.manager} size="sm" />
                      <span className="font-black uppercase">{row.manager}</span>
                    </Link>
                  </td>
                  <td className="px-4 py-4 font-mono text-[12px]">{row.stats.seasons_played}</td>
                  <td className="px-4 py-4 font-mono text-[12px]">{row.stats.total_wins}-{row.stats.total_losses}</td>
                  <td className="px-4 py-4 font-mono text-[12px]">{formatNumber(row.stats.win_percentage, 1)}%</td>
                  <td className="px-4 py-4 font-mono text-[12px] text-accent-gold">{formatNumber(row.stats.total_points, 2)}</td>
                  <td className="px-4 py-4 font-mono text-[12px]">{formatNumber(row.stats.average_points_per_season, 2)}</td>
                  <td className="px-4 py-4 font-mono text-[12px]">{formatNumber(row.stats.average_points_per_game, 2)}</td>
                  <td className="px-4 py-4 font-mono text-[12px]">{row.stats.playoff_appearances} ({formatNumber(row.stats.playoff_rate, 1)}%)</td>
                  <td className="px-4 py-4 font-display text-xl font-black text-accent-gold">{row.stats.championships}</td>
                  <td className="px-4 py-4 font-mono text-[12px]">{row.stats.longest_win_streak}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>
      </section>

      <RecordsUnderAttack milestones={milestones} />

      <section className="px-6 md:px-14 py-14 bg-bg-deep border-b border-border">
        <Reveal>
          <Eyebrow color="gold" className="mb-2 tracking-[0.45em]">CAREER LEADERS</Eyebrow>
          <h2 className="text-display-xs md:text-display-sm font-black tracking-normal leading-none mb-8">Who owns what.</h2>
        </Reveal>
        <Reveal delay={80} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5">
          <LeaderCard label="Most championships" manager={ringsLeader?.manager} value={`${ringsLeader?.stats.championships} rings`} copy={ringsLeader?.stats.championship_years.join(', ')} />
          <LeaderCard label="Most wins" manager={winsLeader?.manager} value={winsLeader?.stats.total_wins} copy={`${winsLeader?.stats.total_losses} losses across ${winsLeader?.stats.seasons_played} seasons`} />
          <LeaderCard label="Most points" manager={pointsLeader?.manager} value={formatNumber(pointsLeader?.stats.total_points, 2)} copy={`${formatNumber(pointsLeader?.stats.average_points_per_season, 2)} per season`} />
          <LeaderCard label="Best win rate · 5+ seasons" manager={rateLeader?.manager} value={`${formatNumber(rateLeader?.stats.win_percentage, 1)}%`} copy={`${rateLeader?.stats.total_wins}-${rateLeader?.stats.total_losses} lifetime`} />
        </Reveal>
      </section>

      <section className="px-6 md:px-14 py-14 border-b border-border">
        <Reveal>
          <Eyebrow color="red" className="mb-2 tracking-[0.45em]">RECORD BOOK</Eyebrow>
          <h2 className="text-display-xs md:text-display-sm font-black tracking-normal leading-none mb-8">The games nobody forgets.</h2>
        </Reveal>
        <Reveal delay={80} className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
          <GameRecordCard label="Highest weekly score" game={weeklyHigh} value={formatNumber(weeklyHigh?.score, 2)} copy={weeklyHigh ? `${weeklyHigh.manager}, Week ${weeklyHigh.week} of ${weeklyHigh.year}` : ''} />
          <GameRecordCard label="Closest regular-season game" game={closestGame} value={formatNumber(closestGame?.margin, 2)} copy={closestGame ? `${closestGame.winner} over ${closestGame.loser}, ${formatNumber(closestGame.winnerScore, 2)}–${formatNumber(closestGame.loserScore, 2)} in ${closestGame.year}` : ''} />
          <GameRecordCard label="Largest title-game margin" game={largestTitleMargin} value={formatNumber(largestTitleMargin?.margin, 2)} copy={largestTitleMargin ? `${largestTitleMargin.winner} over ${largestTitleMargin.runnerUp} in ${largestTitleMargin.year}` : ''} />
        </Reveal>
      </section>
    </>
  );
}
